import { Check, Copy, Link2, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import type { UseConversationShare } from "./useConversationShare";

interface ShareSessionBodyProps {
  share: UseConversationShare;
}

// Renders the correct share-link surface for the current state. Shared by the
// centered ShareConversationDialog and the inline composer SharePopover.
export const ShareSessionBody = ({ share }: ShareSessionBodyProps) => {
  const {
    share: shareState,
    shareUrl,
    isMutating,
    copied,
    handleGenerate,
    handleRevoke,
    handleCopy,
  } = share;

  if (shareState === undefined) {
    return <LoadingSurface />;
  }

  if (shareState === null) {
    return <GenerateSurface onGenerate={handleGenerate} isMutating={isMutating} />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <LinkSurface url={shareUrl ?? ""} copied={copied} onCopy={handleCopy} />
      <Button
        variant="destructive"
        size="sm"
        className="self-start"
        onClick={handleRevoke}
        disabled={isMutating}
        loading={isMutating}
      >
        {isMutating ? "Revoking link" : "Revoke link"}
      </Button>
    </div>
  );
};

const LoadingSurface = () => (
  <div
    className="flex h-11 items-center justify-center rounded-xl border bg-muted/30 text-muted-foreground"
    role="status"
    aria-live="polite"
  >
    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
  </div>
);

interface GenerateSurfaceProps {
  onGenerate: () => void;
  isMutating: boolean;
}

const GenerateSurface = ({ onGenerate, isMutating }: GenerateSurfaceProps) => (
  <button
    type="button"
    onClick={onGenerate}
    disabled={isMutating}
    className="flex h-11 w-full animate-in fade-in-50 items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 text-sm font-medium text-foreground transition-colors cursor-pointer hover:border-primary/40 hover:bg-muted/50 disabled:cursor-default disabled:opacity-60"
  >
    {isMutating ? (
      <Loader2 className="size-4 animate-spin text-primary" aria-hidden="true" />
    ) : (
      <Link2 className="size-4 text-primary" aria-hidden="true" />
    )}
    {isMutating ? "Creating link" : "Generate link"}
  </button>
);

interface LinkSurfaceProps {
  url: string;
  copied: boolean;
  onCopy: () => void;
}

const LinkSurface = ({ url, copied, onCopy }: LinkSurfaceProps) => (
  <div className="relative flex h-11 w-full min-w-0 animate-in fade-in-50 items-center overflow-hidden rounded-xl border bg-muted/30">
    <p className="min-w-0 select-all truncate pl-4 pr-11 font-mono text-xs text-foreground">
      {url}
    </p>
    <Button
      variant="ghost"
      size="icon"
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy link"}
      className="absolute right-1.5 top-1/2 size-7 -translate-y-1/2"
    >
      {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
    </Button>
  </div>
);
